/**
 * Phone Number Utility Functions
 * Handles validation and formatting of American phone numbers
 */

/**
 * Formats phone number input as user types
 * Formats as: (123) 456-7890
 * @param value - The raw phone input value
 * @returns Formatted phone string
 */
export const formatPhone = (value: string): string => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');
  const normalizedDigits =
    digitsOnly.length === 11 && digitsOnly.startsWith('1')
      ? digitsOnly.substring(1)
      : digitsOnly;
  
  // Limit to 10 digits (US phone number)
  const limitedDigits = normalizedDigits.substring(0, 10);
  
  // Format based on length
  if (limitedDigits.length === 0) {
    return '';
  } else if (limitedDigits.length <= 3) {
    // Just area code: (123
    return `(${limitedDigits}`;
  } else if (limitedDigits.length <= 6) {
    // Area code + first 3: (123) 456
    const areaCode = limitedDigits.substring(0, 3);
    const firstPart = limitedDigits.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else {
    // Full number: (123) 456-7890
    const areaCode = limitedDigits.substring(0, 3);
    const firstPart = limitedDigits.substring(3, 6);
    const secondPart = limitedDigits.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  }
};

/**
 * Validates American phone number
 * Follows North American Numbering Plan (NANP) rules
 * @param value - The phone value to validate
 * @returns Object with isValid boolean and error message if invalid
 */
export const validatePhone = (value: string): { isValid: boolean; error?: string } => {
  // Remove all non-digit characters for validation
  const digitsOnly = value.replace(/\D/g, '');
  const normalizedDigits =
    digitsOnly.length === 11 && digitsOnly.startsWith('1')
      ? digitsOnly.substring(1)
      : digitsOnly;
  
  // Allow empty value (will be validated as required field elsewhere)
  if (!value.trim()) {
    return { isValid: true };
  }
  
  // American phone number must be exactly 10 digits
  if (normalizedDigits.length !== 10) {
    return { 
      isValid: false, 
      error: 'Phone number must be exactly 10 digits (e.g., (123) 456-7890)' 
    };
  }
  
  // Extract area code (first 3 digits) and exchange code (middle 3 digits)
  const areaCode = normalizedDigits.substring(0, 3);
  const exchangeCode = normalizedDigits.substring(3, 6);
  
  // NANP Rule 1: Area code cannot start with 0 or 1
  const areaCodeFirstDigit = areaCode.charAt(0);
  if (areaCodeFirstDigit === '0' || areaCodeFirstDigit === '1') {
    return { 
      isValid: false, 
      error: 'Invalid area code: US area codes cannot start with 0 or 1. Valid area codes start with 2-9 (e.g., 212, 310, 415)' 
    };
  }
  
  // NANP Rule 2: Area code cannot have the pattern N11 (where N is 2-9)
  // These are reserved for special services (211, 311, 411, 511, 611, 711, 811, 911)
  const areaCodeSecondDigit = areaCode.charAt(1);
  const areaCodeThirdDigit = areaCode.charAt(2);
  if (areaCodeSecondDigit === '1' && areaCodeThirdDigit === '1') {
    return { 
      isValid: false, 
      error: 'Invalid area code: N11 pattern (e.g., 211, 311, 911) is reserved for special services' 
    };
  }
  
  // NANP Rule 3: Exchange code (middle 3 digits) cannot start with 0 or 1
  const exchangeCodeFirstDigit = exchangeCode.charAt(0);
  if (exchangeCodeFirstDigit === '0' || exchangeCodeFirstDigit === '1') {
    return { 
      isValid: false, 
      error: 'Invalid exchange code: The middle 3 digits cannot start with 0 or 1. Valid format: (XXX) 2XX-XXXX or (XXX) 9XX-XXXX' 
    };
  }
  
  // NANP Rule 4: Exchange code cannot have the pattern N11 (where N is 2-9)
  // These are also reserved for special services
  const exchangeCodeSecondDigit = exchangeCode.charAt(1);
  const exchangeCodeThirdDigit = exchangeCode.charAt(2);
  if (exchangeCodeSecondDigit === '1' && exchangeCodeThirdDigit === '1') {
    return { 
      isValid: false, 
      error: 'Invalid exchange code: N11 pattern is reserved for special services' 
    };
  }
  
  // Optional: Check if area code is all the same digit (e.g., 222, 333) - not typically assigned
  if (areaCode.charAt(0) === areaCode.charAt(1) && areaCode.charAt(1) === areaCode.charAt(2)) {
    // This is a warning but not a hard error - some like 555 are reserved, but we'll allow it
    // Actually, 555 is commonly used for fictional numbers, so we'll skip this check
  }
  
  // Optional: Check if exchange code is all the same digit - usually not assigned
  if (exchangeCode.charAt(0) === exchangeCode.charAt(1) && exchangeCode.charAt(1) === exchangeCode.charAt(2)) {
    // This is also not a hard error, but worth noting
  }
  
  return { isValid: true };
};
